<script>
  // # # # # # # # # # # # # #
  //
  //  Taxonomy listing
  //
  // # # # # # # # # # # # # #

  // *** IMPORTS
  import { onMount } from "svelte";
  import { Router, links } from "svelte-routing";
  import { fade } from "svelte/transition";

  // *** COMPONENTS
  import Brick from "../components/Brick.svelte";
  import Ellipse from "../components/Ellipse.svelte";

  // *** PROPS
  export let title = "";
  export let endpoint = "";
  export let query = "";

  // *** VARIABLES
  let items = [];
  let taxList = [];
  let activeTags = [];
  let taxonomyOverview = query ? false : true;
  let currentQuery = query;
  let loaded = false;
  let imagesLoadedCounter = 0;
  let tagComp = "";

  // *** FUNCTIONS
  const removeTag = tagName => {
    if (activeTags.indexOf(tagName) > -1) {
      return activeTags.filter(t => t != tagName).join("&");
    } else {
      return tagName;
    }
  };

  // --- Get new content if query changes
  $: {
    if (query !== currentQuery) {
      items = [];
      taxonomyOverview = false;
      currentQuery = query;
      imagesLoadedCounter = 0;
      loadData(query);
    }
  }

  $: tagComp = activeTags.join(" / ").toUpperCase();

  const loadData = q => {
    const url = endpoint + (q ? "/tag:" + q : "");
    fetch(url)
      .then(r => r.json())
      .then(arr => {
        items = arr.posts ? arr.posts : [];
        activeTags = arr.activeTags ? arr.activeTags : [];
        if (taxList.length === 0) {
          taxList = Array.from(Object.keys(arr.taxList));
        }
        loaded = true;
      })
      .catch(e => {
        console.error(e);
      });
  };

  loadData(query);

  // *** ON MOUNT
  onMount(async () => {
    window.scrollTo(0, 0);
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .masonry {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    margin-top: 20px;
  }

  .tag-view {
    position: static;
    margin-top: $top-space;
    margin-left: $main-margin;
    padding-bottom: $top-space;
    width: calc(100% - 20px);
    display: none;
    // background: yellow;

    &.active {
      display: block;
    }
  }

  .taxonomy {
    position: static;
    margin-top: $top-space;
    padding-left: $main-margin;
    padding-right: $main-margin;
    padding-bottom: $top-space;
    width: 100%;
    line-height: 1em;
    columns: 3;
    column-gap: 0;
    overflow: scroll;
    background: white;
    // background: red;

    display: none;

    &.active {
      display: inline-block;
    }

    @include screen-size("small") {
      columns: 2;
      width: 100%;
      margin-left: 0;
      height: auto;
      left: unset;
    }

    &__message {
      font-family: $serif-stack;
      line-height: 1.3em;
      font-size: $font-size-large;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
      margin-top: 60px;
      position: relative;

      @include screen-size("small") {
        left: -10px;
      }

      a {
        color: currentColor;
        @include normal-hover-state;
      }
    }

    &__item {
      display: block;
      margin-bottom: 0.25em;
      font-family: $sans-stack;
      font-size: $font-size-medium;
      color: $black;
      border-bottom: none;
      text-align: center;
      text-transform: uppercase;

      @include normal-hover-state;

      &--active {
        color: $grey;
        @include normal-hover-state;
      }

      @include screen-size("small") {
        font-size: $mobile-font-size-small;
      }
    }

    &__header {
      position: relative;
      left: -10px;
      margin-top: 100px;
      width: 100%;
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: $sans-stack;
      @include screen-size("small") {
        flex-wrap: wrap;
      }
    }

    &__header-item {
      display: block;
      font-family: $sans-stack;
      font-size: $font-size-medium;
      color: $black;
      border-bottom: none;
      text-align: center;
      margin-left: $main-margin;
      cursor: pointer;
      text-transform: uppercase;

      @include normal-hover-state;

      &:hover {
        text-decoration: line-through;
      }

      @include screen-size("small") {
        font-size: $mobile-font-size-small;
      }

      &--addition {
        position: relative;
        &:hover {
          border-bottom: none;
          text-decoration: none;
        }
        @include screen-size("small") {
          top: -1px;
          font-size: $mobile-font-size-medium;
        }
      }
    }
  }

  .counter {
    pointer-events: none;
  }
</style>

<svelte:head>
  {#if activeTags.length === 0}
    <title>{title} | COMPLETE WORKS</title>
  {:else}
    <title>{tagComp} | COMPLETE WORKS</title>
  {/if}
</svelte:head>

<Router>

  {#if taxonomyOverview}
    <!-- ************* -->
    <!-- SHOW ALL TAGS -->
    <!-- ************* -->

    <div class="taxonomy" class:active={taxonomyOverview} use:links in:fade>
      {#each taxList as tag}
        {#if activeTags.includes(tag)}
          <!-- REMOVE EL -->
          <a
            class="taxonomy__item taxonomy__item--active"
            href="/tags/{removeTag(tag)}">
            {tag}
          </a>
        {:else}
          <a
            class="taxonomy__item"
            href="/tags/{[...activeTags, tag].join('&')}">
            {tag}
          </a>
        {/if}
      {/each}
    </div>
  {:else}
    <!-- **************** -->
    <!-- LIST ACTIVE TAGS -->
    <!-- **************** -->

    <div class="tag-view" class:active={!taxonomyOverview} in:fade>
      <div class="taxonomy__header" use:links>
        {#if loaded && imagesLoadedCounter == items.length}
          <span
            class="taxonomy__header-item taxonomy__header-item--addition"
            on:click={e => {
              taxonomyOverview = true;
            }}>
            +
          </span>
        {/if}
        {#if loaded && imagesLoadedCounter == items.length}
          {#each activeTags as tag}
            <a href="/tags/{removeTag(tag)}" class="taxonomy__header-item">
              {tag}
            </a>
          {/each}
        {:else}
          <Ellipse />
        {/if}
        {#if items.length > 0}
          <span class="taxonomy__header-item counter">
            ({imagesLoadedCounter})
          </span>
        {/if}
      </div>
      <!-- ************ -->
      <!-- SHOW RESULTS -->
      <!-- ************ -->

      <div class="masonry" in:fade>
        {#if loaded && items.length > 0}
          {#each items as item}
            <Brick
              {item}
              on:brickLoaded={() => {
                imagesLoadedCounter += 1;
              }} />
          {/each}
        {/if}
        {#if loaded && items.length === 0}
          <div class="taxonomy__message">
            <p in:fade>
              No content found.
              <br />
              Try
              <a href="/tags/">another tag</a>
              or
              <a href="/">return to the frontpage</a>
            </p>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</Router>
